import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Package,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Wrench,
} from "lucide-react";
import {
  useNavigate,
} from "react-router-dom";
import { toast } from "sonner";

import {
  gmao,
  machineCatalog as machineCatalogApi,
} from "@/api";
import MachineArchiveModal from "@/components/gmao/MachineArchiveModal";
import MachineFormModal from "@/components/gmao/MachineFormModal";
import PartFormModal from "@/components/gmao/PartFormModal";
import { useAuth } from "@/context/AuthContext";
import { hasPageAccess } from "@/lib/accessPermissions";
import { formatApiError } from "@/lib/api";


const PAGE_SIZE = 25;

const EQUIPMENT_LABELS = {
  machine: "Machine",
  accessory: "Accessoire",
  handpiece: "Pièce à main",
};

const LOCATION_LABELS = {
  in_stock: "En stock",
  reserved: "Réservée",
  installed: "Installée",
  returned: "Retournée",
};

const STATUS_LABELS = {
  active: "Opérationnel",
  maintenance: "En maintenance",
  inactive: "Inactif",
  retired: "Hors service",
};

const STOCK_LABELS = {
  available: "Disponible",
  low_stock: "Stock faible",
  out_of_stock: "Rupture",
};

const BADGE_CLASSES = {
  active:
    "border-[var(--do-success-border)] bg-[var(--do-success-soft)] text-[var(--do-success)]",
  available:
    "border-[var(--do-success-border)] bg-[var(--do-success-soft)] text-[var(--do-success)]",
  installed:
    "border-[var(--do-success-border)] bg-[var(--do-success-soft)] text-[var(--do-success)]",
  maintenance:
    "border-[var(--do-warning-border)] bg-[var(--do-warning-soft)] text-[var(--do-warning)]",
  low_stock:
    "border-[var(--do-warning-border)] bg-[var(--do-warning-soft)] text-[var(--do-warning)]",
  out_of_stock:
    "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]",
  retired:
    "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]",
  archived:
    "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]",
  neutral:
    "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-muted)]",
  accent:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
};


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function equipmentType(machine) {
  return machine.equipmentType || "machine";
}


function locationStatus(machine) {
  return (
    machine.locationStatus ||
    (
      machine.prospectId
        ? "installed"
        : "in_stock"
    )
  );
}


function Badge({
  children,
  tone = "neutral",
}) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-bold ${
        BADGE_CLASSES[tone] ||
        BADGE_CLASSES.neutral
      }`}
    >
      {children}
    </span>
  );
}


function KpiCard({
  icon: Icon,
  label,
  value,
  onClick,
  testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="
        group
        block
        min-h-11
        rounded-2xl
        border
        border-[var(--do-border)]
        bg-[var(--do-bg-solid)]
        p-4
        text-left
        text-[var(--do-text)]
        transition
        hover:-translate-y-0.5
        hover:border-[var(--do-accent)]
        hover:shadow-md
        focus:outline-none
        focus-visible:ring-2
        focus-visible:ring-[var(--do-accent)]
        focus-visible:ring-offset-2
      "
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-xs font-semibold uppercase tracking-wide text-[var(--do-muted)]">
          {label}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Icon
            size={18}
            className="text-[var(--do-accent)]"
          />

          <ChevronRight
            size={16}
            className="text-[var(--do-muted)] transition-transform group-hover:translate-x-0.5"
          />
        </div>
      </div>

      <div className="mt-3 text-3xl font-black text-[var(--do-text)]">
        {value}
      </div>
    </button>
  );
}

function EmptyState({
  search,
  type,
}) {
  return (
    <div
      className="p-10 text-center text-sm text-[var(--do-muted)]"
      data-testid="gmao-parc-empty-state"
    >
      {search
        ? "Aucun résultat ne correspond à votre recherche."
        : type === "parts"
          ? "Aucune pièce enregistrée."
          : "Aucun équipement enregistré dans le parc."}
    </div>
  );
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "fr-FR"
  ).format(parsed);
}


export default function GmaoParcV2() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const canSeeParts = hasPageAccess(
    user,
    "gmao",
    "parts"
  );

  const canSeeMaintenance = hasPageAccess(
    user,
    "gmao",
    "maintenance"
  );

  const canManageMachines = [
    "admin",
    "maintenance_manager",
  ].includes(user?.role);

  const canViewArchives = [
    "admin",
    "manager",
    "maintenance_manager",
  ].includes(user?.actualRole || user?.role);

  const canManageParts = [
    "admin",
    "manager",
    "maintenance_manager",
  ].includes(user?.role);

  const [activeTab, setActiveTab] =
    useState("equipment");

  const [machines, setMachines] =
    useState([]);

  const [parts, setParts] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [catalogItems, setCatalogItems] =
    useState([]);

  const [
    catalogLoadError,
    setCatalogLoadError,
  ] = useState("");

  const [
    maintenancePlans,
    setMaintenancePlans,
  ] = useState([]);

  const [search, setSearch] =
    useState("");

  const [equipmentFilter, setEquipmentFilter] =
    useState("all");

  const [locationFilter, setLocationFilter] =
    useState("all");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [archiveFilter, setArchiveFilter] =
    useState("active");

  const [clientFilter, setClientFilter] =
    useState("all");

  const [categoryFilter, setCategoryFilter] =
    useState("all");

  const [stockFilter, setStockFilter] =
    useState("all");

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const [pageNumber, setPageNumber] =
    useState(1);

  const [saving, setSaving] =
    useState(false);

  const [machineEditorOpen, setMachineEditorOpen] =
    useState(false);

  const [partEditorOpen, setPartEditorOpen] =
    useState(false);

  const [restoreTarget, setRestoreTarget] =
    useState(null);


  const load = useCallback(async ({
    silent = false,
  } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    const machineRequest =
      gmao.listMachines({
        limit: 500,
        include_archived: canViewArchives ? true : undefined,
      });

    const partsRequest =
      canSeeParts
        ? gmao.listParts({
            limit: 1000,
          })
        : Promise.resolve([]);

    const clientsRequest =
      canManageMachines
        ? gmao.getClientOptions()
        : Promise.resolve({ items: [] });

    const maintenanceRequest =
      canSeeMaintenance
        ? gmao.listMaintenancePlans({
            limit: 1000,
          })
        : Promise.resolve([]);

    const catalogRequest =
      canManageMachines
        ? machineCatalogApi.list()
        : Promise.resolve([]);

    const results = await Promise.allSettled([
      machineRequest,
      partsRequest,
      clientsRequest,
      maintenanceRequest,
      catalogRequest,
    ]);

    const [
      machineResult,
      partResult,
      clientResult,
      maintenanceResult,
      catalogResult,
    ] = results;

    if (machineResult.status === "fulfilled") {
      setMachines(
        safeArray(machineResult.value)
      );
    } else {
      setMachines([]);
      setError(
        formatApiError(
          machineResult.reason?.response?.data?.detail
        ) ||
        "Impossible de charger le parc."
      );
    }

    if (catalogResult.status === "fulfilled") {
      setCatalogItems(
        safeArray(catalogResult.value)
      );
      setCatalogLoadError("");
    } else {
      setCatalogItems([]);
      setCatalogLoadError(
        "catalog_load_failed"
      );
    }

    if (partResult.status === "fulfilled") {
      setParts(
        safeArray(partResult.value)
      );
    } else {
      setParts([]);
    }

    if (clientResult.status === "fulfilled") {
      setClients(
        safeArray(clientResult.value)
      );
    } else {
      setClients([]);
    }

    if (maintenanceResult.status === "fulfilled") {
      setMaintenancePlans(
        Array.isArray(
          maintenanceResult.value
        )
          ? maintenanceResult.value
          : safeArray(
              maintenanceResult.value?.items
            )
      );
    } else {
      setMaintenancePlans([]);
    }

    setLoading(false);
    setRefreshing(false);
  }, [
    canManageMachines,
    canSeeMaintenance,
    canSeeParts,
    canViewArchives,
  ]);


  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    if (
      activeTab === "parts" &&
      !canSeeParts
    ) {
      setActiveTab("equipment");
    }
  }, [
    activeTab,
    canSeeParts,
  ]);


  useEffect(() => {
    setPageNumber(1);
  }, [
    activeTab,
    categoryFilter,
    clientFilter,
    equipmentFilter,
    locationFilter,
    search,
    statusFilter,
    stockFilter,
    archiveFilter,
  ]);


  const clientById = useMemo(
    () =>
      Object.fromEntries(
        clients
          .filter((item) => item?.id)
          .map((item) => [
            item.id,
            item,
          ])
      ),
    [clients]
  );


  const clientName = useCallback(
    (machine) => {
      if (machine.clientName) {
        return machine.clientName;
      }

      const prospect =
        clientById[
          machine.prospectId
        ];

      if (!prospect) {
        return "";
      }

      return (
        prospect.company ||
        [
          prospect.firstName,
          prospect.lastName,
        ]
          .filter(Boolean)
          .join(" ") ||
        prospect.email ||
        ""
      );
    },
    [clientById]
  );


  const maintenanceByMachine = useMemo(
    () => {
      const result = {};

      maintenancePlans.forEach((plan) => {
        if (
          !plan?.machineId ||
          plan.isActive === false
        ) {
          return;
        }

        const existing =
          result[plan.machineId];

        if (
          !existing ||
          String(plan.nextDueAt || "") <
            String(existing.nextDueAt || "")
        ) {
          result[plan.machineId] = plan;
        }
      });

      return result;
    },
    [maintenancePlans]
  );


  const clientOptions = useMemo(
    () =>
      Array.from(
        new Map(
          machines
            .map((machine) => [
              machine.prospectId ||
                clientName(machine),
              clientName(machine),
            ])
            .filter(
              ([id, label]) =>
                id && label
            )
        )
      ).sort(
        (left, right) =>
          left[1].localeCompare(
            right[1],
            "fr"
          )
      ),
    [
      clientName,
      machines,
    ]
  );


  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          parts
            .map((part) => part.category)
            .filter(Boolean)
        )
      ).sort(
        (left, right) =>
          left.localeCompare(
            right,
            "fr"
          )
      ),
    [parts]
  );


  const filteredMachines = useMemo(
    () => {
      const cleanSearch =
        search.trim().toLocaleLowerCase(
          "fr"
        );

      return machines.filter((machine) => {
        const type =
          equipmentType(machine);

        const location =
          locationStatus(machine);

        const client =
          clientName(machine);

        if (
          archiveFilter === "active" &&
          machine.isArchived === true
        ) {
          return false;
        }

        if (
          archiveFilter === "archived" &&
          machine.isArchived !== true
        ) {
          return false;
        }

        if (
          equipmentFilter !== "all" &&
          type !== equipmentFilter
        ) {
          return false;
        }

        if (
          locationFilter !== "all" &&
          location !== locationFilter
        ) {
          return false;
        }

        if (
          statusFilter !== "all" &&
          machine.status !== statusFilter
        ) {
          return false;
        }

        if (
          clientFilter !== "all" &&
          (
            machine.prospectId ||
            client
          ) !== clientFilter
        ) {
          return false;
        }

        if (!cleanSearch) {
          return true;
        }

        return [
          machine.name,
          machine.model,
          machine.serialNumber,
          client,
          machine.stockLocation,
          machine.siteName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("fr")
          .includes(cleanSearch);
      });
    },
    [
      archiveFilter,
      clientFilter,
      clientName,
      equipmentFilter,
      locationFilter,
      machines,
      search,
      statusFilter,
    ]
  );


  const filteredParts = useMemo(
    () => {
      const cleanSearch =
        search.trim().toLocaleLowerCase(
          "fr"
        );

      return parts.filter((part) => {
        if (
          categoryFilter !== "all" &&
          part.category !== categoryFilter
        ) {
          return false;
        }

        if (
          stockFilter !== "all" &&
          part.stockStatus !== stockFilter
        ) {
          return false;
        }

        if (!cleanSearch) {
          return true;
        }

        return [
          part.reference,
          part.name,
          part.category,
          part.location,
          part.supplier,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("fr")
          .includes(cleanSearch);
      });
    },
    [
      categoryFilter,
      parts,
      search,
      stockFilter,
    ]
  );


  const visibleItems =
    activeTab === "parts"
      ? filteredParts
      : filteredMachines;

  const totalPages = Math.max(
    1,
    Math.ceil(
      visibleItems.length /
      PAGE_SIZE
    )
  );

  const safePage = Math.min(
    pageNumber,
    totalPages
  );

  const paginatedItems =
    visibleItems.slice(
      (safePage - 1) * PAGE_SIZE,
      safePage * PAGE_SIZE
    );


  const resetFilters = () => {
    setSearch("");
    setEquipmentFilter("all");
    setLocationFilter("all");
    setStatusFilter("all");
    setArchiveFilter("active");
    setClientFilter("all");
    setCategoryFilter("all");
    setStockFilter("all");
  };


  const applyKpiFilter = (
    filter
  ) => {
    setActiveTab("equipment");
    setEquipmentFilter("all");
    setStatusFilter("all");
    setArchiveFilter("active");
    setClientFilter("all");

    if (filter === "all") {
      setLocationFilter("all");
    } else if (
      filter === "maintenance"
    ) {
      setLocationFilter("all");
      setStatusFilter("maintenance");
    } else {
      setLocationFilter(filter);
    }
  };


  const saveQuickClient = async (
    payload
  ) => {
    const created =
      await gmao.createQuickClient(
        payload
      );

    setClients((current) => [
      ...current.filter(
        (item) =>
          item?.id !== created?.id
      ),
      created,
    ]);

    return created;
  };


  const saveMachine = async (
    payload
  ) => {
    setSaving(true);

    try {
      await gmao.createMachine(
        payload
      );

      toast.success(
        "Équipement créé"
      );

      setMachineEditorOpen(false);
      await load({
        silent: true,
      });
    } catch (saveError) {
      toast.error(
        formatApiError(
          saveError.response?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const restoreMachine = async () => {
    if (!restoreTarget) {
      return;
    }

    setSaving(true);

    try {
      await gmao.restoreMachine(restoreTarget.id);
      toast.success("Machine restaurée");
      setRestoreTarget(null);
      await load({ silent: true });
    } catch (restoreError) {
      toast.error(
        formatApiError(
          restoreError.response?.data?.detail
        ) || "Impossible de restaurer la machine."
      );
    } finally {
      setSaving(false);
    }
  };


  const savePart = async (
    payload
  ) => {
    setSaving(true);

    try {
      await gmao.createPart(
        payload
      );

      toast.success(
        "Pièce créée"
      );

      setPartEditorOpen(false);
      await load({
        silent: true,
      });
    } catch (saveError) {
      toast.error(
        formatApiError(
          saveError.response?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const currentStart =
    visibleItems.length
      ? (
          (safePage - 1) *
          PAGE_SIZE
        ) + 1
      : 0;

  const currentEnd = Math.min(
    safePage * PAGE_SIZE,
    visibleItems.length
  );


  return (
    <main
      className="acta-gmao-page-standard w-full min-w-0 max-w-full p-4 sm:p-5 lg:p-8"
      data-testid="gmao-parc-v2-page"
    >
      <section
        className="grid grid-cols-[repeat(auto-fit,minmax(min(11rem,100%),1fr))] gap-3"
        data-testid="gmao-parc-kpis"
      >
        <KpiCard
          icon={Cpu}
          label="Équipements"
          value={machines.filter((machine) => machine.isArchived !== true).length}
          onClick={() =>
            applyKpiFilter("all")
          }
          testId="gmao-parc-kpi-equipment"
        />

        <KpiCard
          icon={Box}
          label="En stock"
          value={
            machines.filter(
              (machine) =>
                machine.isArchived !== true &&
                locationStatus(machine) ===
                "in_stock"
            ).length
          }
          onClick={() =>
            applyKpiFilter("in_stock")
          }
          testId="gmao-parc-kpi-in-stock"
        />

        <KpiCard
          icon={Package}
          label="Installés"
          value={
            machines.filter(
              (machine) =>
                machine.isArchived !== true &&
                locationStatus(machine) ===
                "installed"
            ).length
          }
          onClick={() =>
            applyKpiFilter("installed")
          }
          testId="gmao-parc-kpi-installed"
        />

        <KpiCard
          icon={Wrench}
          label="En maintenance"
          value={
            machines.filter(
              (machine) =>
                machine.isArchived !== true &&
                machine.status ===
                "maintenance"
            ).length
          }
          onClick={() =>
            applyKpiFilter(
              "maintenance"
            )
          }
          testId="gmao-parc-kpi-maintenance"
        />
      </section>

      <section
        className="acta-gmao-parc-toolbar mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3"
        data-testid="gmao-parc-toolbar"
      >
        <div className="acta-gmao-parc-toolbar-content flex min-w-0 flex-wrap items-center gap-2">
          <label className="relative min-w-0 flex-[1_1_18rem]">
            <Search
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />

            <span className="sr-only">
              Rechercher dans le parc
            </span>

            <input
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder={
                activeTab === "parts"
                  ? "Rechercher une pièce…"
                  : "Rechercher un équipement…"
              }
              className="h-10 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-3 text-sm outline-none focus:border-[var(--do-accent)]"
              data-testid="gmao-parc-search"
            />
          </label>

          <div className="acta-gmao-parc-toolbar-filter-grid grid min-w-0 flex-[1_1_36rem] grid-cols-[repeat(auto-fit,minmax(min(10rem,100%),1fr))] gap-2">
            {activeTab === "equipment" ? (
              <>
                <select
                  value={equipmentFilter}
                  onChange={(event) =>
                    setEquipmentFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-type-filter"
                >
                  <option value="all">
                    Tous les types
                  </option>
                  <option value="machine">
                    Machines
                  </option>
                  <option value="accessory">
                    Accessoires
                  </option>
                  <option value="handpiece">
                    Pièces à main
                  </option>
                </select>

                <select
                  value={locationFilter}
                  onChange={(event) =>
                    setLocationFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-location-filter"
                >
                  <option value="all">
                    Toutes les localisations
                  </option>
                  {Object.entries(
                    LOCATION_LABELS
                  ).map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ))}
                </select>

                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-status-filter"
                >
                  <option value="all">
                    Tous les états
                  </option>
                  {Object.entries(
                    STATUS_LABELS
                  ).map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ))}
                </select>

                {canViewArchives && (
                  <select
                    value={archiveFilter}
                    onChange={(event) =>
                      setArchiveFilter(event.target.value)
                    }
                    className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                    data-testid="gmao-parc-archive-filter"
                  >
                    <option value="active">Machines actives</option>
                    <option value="archived">Machines archivées</option>
                    <option value="all">Actives et archivées</option>
                  </select>
                )}

                <select
                  value={clientFilter}
                  onChange={(event) =>
                    setClientFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-client-filter"
                >
                  <option value="all">
                    Tous les clients
                  </option>
                  {clientOptions.map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </>
            ) : (
              <>
                <select
                  value={categoryFilter}
                  onChange={(event) =>
                    setCategoryFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-category-filter"
                >
                  <option value="all">
                    Toutes les catégories
                  </option>
                  {categoryOptions.map(
                    (category) => (
                      <option
                        key={category}
                        value={category}
                      >
                        {category}
                      </option>
                    )
                  )}
                </select>

                <select
                  value={stockFilter}
                  onChange={(event) =>
                    setStockFilter(
                      event.target.value
                    )
                  }
                  className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                  data-testid="gmao-parc-stock-filter"
                >
                  <option value="all">
                    Tous les stocks
                  </option>
                  {Object.entries(
                    STOCK_LABELS
                  ).map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="acta-gmao-parc-toolbar-actions flex min-w-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="h-10 rounded-xl px-3 text-sm font-bold text-[var(--do-muted)] transition hover:bg-[var(--do-bg-2)]"
            >
              Réinitialiser
            </button>

            <button
              type="button"
              onClick={() =>
                load({
                  silent: true,
                })
              }
              disabled={refreshing}
              aria-label="Actualiser le parc"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--do-border)] transition hover:bg-[var(--do-bg-2)] disabled:opacity-50"
              data-testid="refresh-gmao-parc"
            >
              <RefreshCw
                size={17}
                className={
                  refreshing
                    ? "animate-spin"
                    : ""
                }
              />
            </button>

            {(
              activeTab === "equipment"
                ? canManageMachines
                : canManageParts
            ) ? (
              <button
                type="button"
                onClick={() => {
                  if (
                    activeTab ===
                    "equipment"
                  ) {
                    setMachineEditorOpen(
                      true
                    );
                  } else {
                    setPartEditorOpen(
                      true
                    );
                  }
                }}
                className="flex h-10 items-center gap-2 whitespace-nowrap rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] transition hover:opacity-90"
                data-testid="gmao-parc-create"
              >
                <Plus size={17} />
                {activeTab === "parts"
                  ? "Nouvelle pièce"
                  : "Nouvel équipement"}
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className="mt-4 overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
        <div
          className="flex border-b border-[var(--do-border)] px-3"
          role="tablist"
          aria-label="Contenu du parc"
        >
          <button
            type="button"
            role="tab"
            aria-selected={
              activeTab ===
              "equipment"
            }
            onClick={() =>
              setActiveTab("equipment")
            }
            className={`border-b-2 px-4 py-4 text-sm font-black ${
              activeTab === "equipment"
                ? "border-[var(--do-accent)] text-[var(--do-accent)]"
                : "border-transparent text-[var(--do-muted)]"
            }`}
            data-testid="gmao-parc-equipment-tab"
          >
            Équipements
          </button>

          {canSeeParts ? (
            <button
              type="button"
              role="tab"
              aria-selected={
                activeTab === "parts"
              }
              onClick={() =>
                setActiveTab("parts")
              }
              className={`border-b-2 px-4 py-4 text-sm font-black ${
                activeTab === "parts"
                  ? "border-[var(--do-accent)] text-[var(--do-accent)]"
                  : "border-transparent text-[var(--do-muted)]"
              }`}
              data-testid="gmao-parc-parts-tab"
            >
              Pièces
            </button>
          ) : null}
        </div>

        {loading ? (
          <div
            className="p-10 text-center text-sm text-[var(--do-muted)]"
            data-testid="gmao-parc-loading"
          >
            Chargement du parc…
          </div>
        ) : error ? (
          <div
            className="p-10 text-center"
            data-testid="gmao-parc-error"
          >
            <div className="font-bold text-[var(--do-danger)]">
              {error}
            </div>

            <button
              type="button"
              onClick={() => load()}
              className="mt-4 rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold"
            >
              Réessayer
            </button>
          </div>
        ) : !visibleItems.length ? (
          <EmptyState
            search={search}
            type={activeTab}
          />
        ) : activeTab === "equipment" ? (
          <>
            <div className="acta-gmao-parc-equipment-table-scroll hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1050px] text-sm">
                <thead>
                  <tr className="border-b border-[var(--do-border)] text-left text-xs font-bold text-[var(--do-muted)]">
                    <th className="px-4 py-3">
                      Type
                    </th>
                    <th className="px-4 py-3">
                      Équipement
                    </th>
                    <th className="px-4 py-3">
                      N° de série
                    </th>
                    <th className="px-4 py-3">
                      Localisation
                    </th>
                    <th className="px-4 py-3">
                      Client / emplacement
                    </th>
                    <th className="px-4 py-3">
                      État
                    </th>
                    <th className="px-4 py-3">
                      Maintenance
                    </th>
                    <th className="px-4 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedItems.map(
                    (machine) => {
                      const type =
                        equipmentType(
                          machine
                        );

                      const location =
                        locationStatus(
                          machine
                        );

                      const maintenance =
                        maintenanceByMachine[
                          machine.id
                        ];

                      return (
                        <tr
                          key={machine.id}
                          tabIndex={0}
                          onClick={() =>
                            navigate(
                              `/app/gmao/v2/parc/${encodeURIComponent(
                                machine.id
                              )}`
                            )
                          }
                          onKeyDown={(event) => {
                            if (
                              event.key ===
                                "Enter" ||
                              event.key === " "
                            ) {
                              navigate(
                                `/app/gmao/v2/parc/${encodeURIComponent(
                                  machine.id
                                )}`
                              );
                            }
                          }}
                          className="cursor-pointer border-b border-[var(--do-border)] transition last:border-b-0 hover:bg-[var(--do-bg-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--do-accent)]"
                          data-testid={`gmao-parc-machine-${machine.id}`}
                        >
                          <td className="px-4 py-3">
                            <Badge tone="accent">
                              {EQUIPMENT_LABELS[
                                type
                              ] || "Équipement"}
                            </Badge>
                          </td>

                          <td className="px-4 py-3">
                            <div className="font-bold text-[var(--do-text)]">
                              {machine.name ||
                                "Équipement"}
                            </div>
                            <div className="text-xs text-[var(--do-muted)]">
                              {machine.model ||
                                "Modèle non renseigné"}
                            </div>
                          </td>

                          <td className="px-4 py-3 font-mono text-xs">
                            {machine.serialNumber ||
                              "—"}
                          </td>

                          <td className="px-4 py-3">
                            <Badge
                              tone={
                                location ===
                                "installed"
                                  ? "installed"
                                  : "neutral"
                              }
                            >
                              {LOCATION_LABELS[
                                location
                              ] || location}
                            </Badge>
                          </td>

                          <td className="px-4 py-3">
                            {location ===
                            "installed"
                              ? clientName(
                                  machine
                                ) || "Client non renseigné"
                              : machine.stockLocation ||
                                machine.siteName ||
                                "Emplacement non renseigné"}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <Badge
                                tone={
                                  machine.status ||
                                  "neutral"
                                }
                              >
                                {STATUS_LABELS[
                                  machine.status
                                ] ||
                                  machine.status ||
                                  "Non renseigné"}
                              </Badge>
                              {machine.isArchived === true && (
                                <Badge tone="archived">Archivée</Badge>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            {maintenance
                              ? formatDate(
                                  maintenance.nextDueAt
                                )
                              : "—"}
                          </td>

                          <td className="px-4 py-3 text-right">
                            {machine.isArchived === true && canViewArchives ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setRestoreTarget(machine);
                                }}
                                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-xs font-bold"
                                data-testid={`gmao-parc-restore-${machine.id}`}
                              >
                                <RotateCcw size={15} />
                                Restaurer
                              </button>
                            ) : (
                              <ChevronRight
                                size={18}
                                className="ml-auto text-[var(--do-muted)]"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 p-3 md:hidden">
              {paginatedItems.map(
                (machine) => {
                  const type =
                    equipmentType(machine);

                  const location =
                    locationStatus(machine);

                  return (
                    <div
                      key={machine.id}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(
                          `/app/gmao/v2/parc/${encodeURIComponent(
                            machine.id
                          )}`
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          navigate(
                            `/app/gmao/v2/parc/${encodeURIComponent(
                              machine.id
                            )}`
                          );
                        }
                      }}
                      className="rounded-xl border border-[var(--do-border)] p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Badge tone="accent">
                            {EQUIPMENT_LABELS[
                              type
                            ] || "Équipement"}
                          </Badge>

                          <div className="mt-3 font-bold">
                            {machine.name ||
                              "Équipement"}
                          </div>

                          <div className="mt-1 text-xs text-[var(--do-muted)]">
                            {machine.serialNumber ||
                              "Sans numéro de série"}
                          </div>
                        </div>

                        <ChevronRight
                          size={18}
                        />
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Badge
                          tone={
                            location ===
                            "installed"
                              ? "installed"
                              : "neutral"
                          }
                        >
                          {LOCATION_LABELS[
                            location
                          ] || location}
                        </Badge>

                        <Badge
                          tone={
                            machine.status ||
                            "neutral"
                          }
                        >
                          {STATUS_LABELS[
                            machine.status
                          ] ||
                            machine.status ||
                            "Non renseigné"}
                        </Badge>
                        {machine.isArchived === true && (
                          <Badge tone="archived">Archivée</Badge>
                        )}
                      </div>

                      <div className="mt-3 text-sm text-[var(--do-muted)]">
                        {location === "installed"
                          ? clientName(
                              machine
                            ) ||
                            "Client non renseigné"
                          : machine.stockLocation ||
                            "Emplacement non renseigné"}
                      </div>

                      {machine.isArchived === true && canViewArchives && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRestoreTarget(machine);
                          }}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-xs font-bold"
                          data-testid={`gmao-parc-mobile-restore-${machine.id}`}
                        >
                          <RotateCcw size={15} />
                          Restaurer
                        </button>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-[var(--do-border)] text-left text-xs font-bold text-[var(--do-muted)]">
                    <th className="px-4 py-3">
                      Référence
                    </th>
                    <th className="px-4 py-3">
                      Pièce
                    </th>
                    <th className="px-4 py-3">
                      Catégorie
                    </th>
                    <th className="px-4 py-3">
                      Disponible
                    </th>
                    <th className="px-4 py-3">
                      Seuil
                    </th>
                    <th className="px-4 py-3">
                      Emplacement
                    </th>
                    <th className="px-4 py-3">
                      État
                    </th>
                    <th className="px-4 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedItems.map(
                    (part) => (
                      <tr
                        key={part.id}
                        tabIndex={0}
                        onClick={() =>
                          navigate(
                            `/app/gmao/parts/${encodeURIComponent(
                              part.id
                            )}`
                          )
                        }
                        onKeyDown={(event) => {
                          if (
                            event.key ===
                              "Enter" ||
                            event.key === " "
                          ) {
                            navigate(
                              `/app/gmao/parts/${encodeURIComponent(
                                part.id
                              )}`
                            );
                          }
                        }}
                        className="cursor-pointer border-b border-[var(--do-border)] transition last:border-b-0 hover:bg-[var(--do-bg-2)]"
                        data-testid={`gmao-parc-part-${part.id}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-bold">
                          {part.reference ||
                            "—"}
                        </td>
                        <td className="px-4 py-3 font-bold">
                          {part.name ||
                            "Pièce"}
                        </td>
                        <td className="px-4 py-3">
                          {part.category ||
                            "—"}
                        </td>
                        <td className="px-4 py-3 font-mono font-bold">
                          {part.quantityOnHand ??
                            0}{" "}
                          {part.unit || ""}
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {part.minimumQuantity ??
                            0}
                        </td>
                        <td className="px-4 py-3">
                          {part.location ||
                            "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            tone={
                              part.stockStatus ||
                              "neutral"
                            }
                          >
                            {STOCK_LABELS[
                              part.stockStatus
                            ] ||
                              part.stockStatus ||
                              "Non renseigné"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChevronRight
                            size={18}
                            className="ml-auto text-[var(--do-muted)]"
                          />
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 p-3 md:hidden">
              {paginatedItems.map(
                (part) => (
                  <button
                    type="button"
                    key={part.id}
                    onClick={() =>
                      navigate(
                        `/app/gmao/parts/${encodeURIComponent(
                          part.id
                        )}`
                      )
                    }
                    className="rounded-xl border border-[var(--do-border)] p-4 text-left"
                  >
                    <div className="flex justify-between gap-3">
                      <div>
                        <div className="font-mono text-xs text-[var(--do-muted)]">
                          {part.reference ||
                            "Sans référence"}
                        </div>
                        <div className="mt-1 font-bold">
                          {part.name ||
                            "Pièce"}
                        </div>
                      </div>

                      <ChevronRight
                        size={18}
                      />
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="font-mono font-bold">
                        {part.quantityOnHand ??
                          0}{" "}
                        {part.unit || ""}
                      </div>

                      <Badge
                        tone={
                          part.stockStatus ||
                          "neutral"
                        }
                      >
                        {STOCK_LABELS[
                          part.stockStatus
                        ] ||
                          part.stockStatus ||
                          "Non renseigné"}
                      </Badge>
                    </div>
                  </button>
                )
              )}
            </div>
          </>
        )}

        {!loading &&
        !error &&
        visibleItems.length ? (
          <div className="flex flex-col gap-3 border-t border-[var(--do-border)] px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[var(--do-muted)]">
              {currentStart}–{currentEnd} sur{" "}
              {visibleItems.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() =>
                  setPageNumber(
                    (current) =>
                      Math.max(
                        1,
                        current - 1
                      )
                  )
                }
                aria-label="Page précédente"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="min-w-20 text-center font-mono text-xs">
                Page {safePage} /{" "}
                {totalPages}
              </div>

              <button
                type="button"
                disabled={
                  safePage >= totalPages
                }
                onClick={() =>
                  setPageNumber(
                    (current) =>
                      Math.min(
                        totalPages,
                        current + 1
                      )
                  )
                }
                aria-label="Page suivante"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {restoreTarget && (
        <MachineArchiveModal
          machine={restoreTarget}
          action="restore"
          saving={saving}
          onClose={() => {
            if (!saving) setRestoreTarget(null);
          }}
          onConfirm={restoreMachine}
        />
      )}

      <MachineFormModal
        open={machineEditorOpen}
        mode="create"
        prospects={clients}
        machines={machines}
        catalogItems={catalogItems}
        catalogLoadError={catalogLoadError}
        onReloadCatalog={() =>
          load({
            silent: true,
          })
        }
        onCreateClient={saveQuickClient}
        saving={saving}
        onClose={() =>
          setMachineEditorOpen(false)
        }
        onSubmit={saveMachine}
      />

      <PartFormModal
        open={partEditorOpen}
        mode="create"
        saving={saving}
        onClose={() =>
          setPartEditorOpen(false)
        }
        onSubmit={savePart}
      />
    </main>
  );
}
